import {
	type FC,
	type HTMLAttributes,
	type KeyboardEvent,
	type PointerEvent,
	type Ref,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { clamp, cn } from "../../lib/utils";
import { trackingAttrs } from "../../utils";

export const RESIZE_HANDLE_KEY_STEP = 16;

type ResizeHandleProps = Omit<
	HTMLAttributes<HTMLDivElement>,
	"onChange" | "role" | "children" | "tabIndex"
> & {
	ref?: Ref<HTMLDivElement>;
	width: number;
	min: number;
	max: number;
	defaultWidth: number;
	onWidthChange: (width: number) => void;
	/** Fires with the final width when a drag ends, and after each key press and reset. */
	onWidthCommit?: (width: number) => void;
	step?: number;
	label?: string;
	trackingId?: string;
	testId?: string;
};

/**
 * A vertical drag handle for the left edge of a right-docked panel. Place it inside a
 * `relative` panel: it straddles the border with a 14px hit area. Dragging left widens the
 * panel (the right edge stays fixed), double click resets, and once focused the arrow keys
 * move it in 16px steps (left widens). The width stays controlled by the caller, which owns
 * persistence and where `min` / `max` come from.
 */
export const ResizeHandle: FC<ResizeHandleProps> = ({
	width,
	min,
	max,
	defaultWidth,
	onWidthChange,
	onWidthCommit,
	step = RESIZE_HANDLE_KEY_STEP,
	label = "Resize panel",
	className,
	trackingId,
	testId = "resize-handle",
	ref,
	...rest
}) => {
	const [dragging, setDragging] = useState(false);
	const dragOrigin = useRef({ x: 0, width: 0 });
	const lastDragWidth = useRef<number | undefined>(undefined);
	const limits = useRef({ min, max, onWidthChange, onWidthCommit });
	limits.current = { min, max, onWidthChange, onWidthCommit };

	const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
		if (event.button !== 0) return;
		event.preventDefault();
		dragOrigin.current = { x: event.clientX, width };
		lastDragWidth.current = undefined;
		setDragging(true);
	};

	const handlePointerMove = useCallback((event: globalThis.PointerEvent) => {
		const { min: lo, max: hi, onWidthChange: emit } = limits.current;
		const delta = dragOrigin.current.x - event.clientX;
		const next = clamp(dragOrigin.current.width + delta, lo, hi);
		lastDragWidth.current = next;
		emit(next);
	}, []);

	useEffect(() => {
		if (!dragging) return;
		const stop = () => {
			setDragging(false);
			const finalWidth = lastDragWidth.current;
			lastDragWidth.current = undefined;
			if (finalWidth !== undefined) limits.current.onWidthCommit?.(finalWidth);
		};
		const previousCursor = document.body.style.cursor;
		const previousSelect = document.body.style.userSelect;
		document.body.style.cursor = "col-resize";
		document.body.style.userSelect = "none";
		window.addEventListener("pointermove", handlePointerMove);
		window.addEventListener("pointerup", stop);
		window.addEventListener("pointercancel", stop);
		return () => {
			document.body.style.cursor = previousCursor;
			document.body.style.userSelect = previousSelect;
			window.removeEventListener("pointermove", handlePointerMove);
			window.removeEventListener("pointerup", stop);
			window.removeEventListener("pointercancel", stop);
		};
	}, [dragging, handlePointerMove]);

	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "ArrowLeft") {
			event.preventDefault();
			const next = clamp(width + step, min, max);
			onWidthChange(next);
			onWidthCommit?.(next);
		} else if (event.key === "ArrowRight") {
			event.preventDefault();
			const next = clamp(width - step, min, max);
			onWidthChange(next);
			onWidthCommit?.(next);
		}
	};

	return (
		<div
			ref={ref}
			role="separator"
			aria-orientation="vertical"
			aria-label={label}
			aria-valuenow={width}
			aria-valuemin={min}
			aria-valuemax={max}
			tabIndex={0}
			title="Drag to resize · double-click to reset"
			data-testid={testId}
			data-dragging={dragging || undefined}
			className={cn(
				"group absolute inset-y-0 -left-[7px] z-40 flex w-[14px] cursor-col-resize touch-none select-none items-center justify-center outline-none",
				className,
			)}
			onPointerDown={handlePointerDown}
			onDoubleClick={() => {
				const next = clamp(defaultWidth, min, max);
				onWidthChange(next);
				onWidthCommit?.(next);
			}}
			onKeyDown={handleKeyDown}
			{...trackingAttrs(trackingId)}
			{...rest}
		>
			<div
				aria-hidden
				className={cn(
					"absolute inset-y-0 left-1/2 w-[2px] -translate-x-1/2 bg-transparent transition-colors",
					"group-hover:bg-bc-focus group-focus-visible:bg-bc-focus group-data-[dragging]:bg-brand-accent",
				)}
			/>
			<div
				aria-hidden
				className={cn(
					"relative flex h-9 w-2 flex-col items-center justify-center gap-[3px] rounded-[4px] border border-bc-strong bg-surface-base transition-colors",
					"group-hover:border-brand-accent group-hover:bg-brand-accent group-focus-visible:border-brand-accent group-focus-visible:bg-brand-accent",
					"group-data-[dragging]:border-brand-accent group-data-[dragging]:bg-brand-accent",
				)}
			>
				{[0, 1, 2].map((dot) => (
					<span
						key={dot}
						className="h-[2px] w-[2px] rounded-full bg-bc-stronger group-hover:bg-bc-inverse group-focus-visible:bg-bc-inverse group-data-[dragging]:bg-bc-inverse"
					/>
				))}
			</div>
			{dragging && (
				<div
					aria-hidden
					data-testid={`${testId}-readout`}
					className="pointer-events-none absolute right-[calc(100%+4px)] top-1/2 -translate-y-1/2 whitespace-nowrap rounded bg-surface-primary px-1.5 py-0.5 font-mono text-[11px] font-medium leading-4 text-inverse"
				>
					{Math.round(width)} px
				</div>
			)}
		</div>
	);
};
ResizeHandle.displayName = "ResizeHandle";

export type { ResizeHandleProps };
